import React from 'react';
import { Box, Button, EmptyState, Flex, Text, TextField, LoadingOrbit, TagBadge, badgeVariantToTagColor } from '@chatsdet/ui';
import { CaretDown, CaretRight, MagnifyingGlass, Funnel, Warning, Terminal } from '@phosphor-icons/react';
import type { ConsoleLogEntry } from '@chatsdet/types';
import { useIssueConsoleLogs } from '../../../../hooks/useIssuesV2';

type ConsoleErrorsTabProps = {
  /** Issue ID for fetching console logs */
  issueId: string;
  /** Occurrence ID for fetching console logs */
  occurrenceId: string | null;
  /** Legacy: single console log excerpt (fallback) */
  consoleLogExcerpt?: string | null;
};

type FilterMode = 'errors' | 'all';
type LogLevel = ConsoleLogEntry['type'];

function getLevelBadgeVariant(
  level: LogLevel
): 'destructive' | 'warning' | 'default' | 'secondary' {
  switch (level) {
    case 'error':
      return 'destructive';
    case 'warning':
      return 'warning';
    case 'info':
      return 'default';
    case 'log':
    case 'debug':
    default:
      return 'secondary';
  }
}

function getLevelLabel(level: LogLevel): string {
  return level.toUpperCase();
}

function isErrorOrWarning(level: LogLevel): boolean {
  return level === 'error' || level === 'warning';
}

function formatTimestamp(timestamp: number | null): string {
  if (timestamp === null) return '';
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    });
  } catch {
    return '';
  }
}

/** Safely render location — handles legacy S3 data where location is an object instead of string */
function formatLocation(location: unknown): string | null {
  if (typeof location === 'string') return location;
  if (typeof location === 'object' && location !== null) {
    const loc = location as Record<string, unknown>;
    return `${loc.url}:${loc.lineNumber}:${loc.columnNumber}`;
  }
  return null;
}

function ConsoleLogRow({
  log,
  isExpanded,
  onToggle,
}: {
  log: ConsoleLogEntry;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const isLongMessage = log.text.length > 200 || log.text.includes('\n');
  const truncatedText =
    isLongMessage && !isExpanded ? log.text.slice(0, 200).replace(/\n/g, ' ') + '...' : log.text;

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-start gap-3 hover:bg-muted/50 transition-colors text-left"
      >
        <span className="text-muted-foreground mt-0.5">
          {isLongMessage ? (
            isExpanded ? (
              <CaretDown size={12} />
            ) : (
              <CaretRight size={12} />
            )
          ) : (
            <span className="w-3 inline-block" />
          )}
        </span>
        <TagBadge name={getLevelLabel(log.type)} color={badgeVariantToTagColor(getLevelBadgeVariant(log.type))} hideDot size="sm" className="w-16 justify-center text-[10px] shrink-0" />
        {log.timestamp !== null && (
          <Text size="xs" className="text-muted-foreground whitespace-nowrap shrink-0 font-mono">
            {formatTimestamp(log.timestamp)}
          </Text>
        )}
        <Text
          size="xs"
          className={`font-mono flex-1 ${isExpanded ? 'whitespace-pre-wrap' : 'truncate'}`}
        >
          {truncatedText}
        </Text>
      </button>

      {isExpanded && (
        <div className="px-4 py-3 bg-muted/30 space-y-3 border-t border-border ml-6">
          <Flex direction="column" gap="xs">
            <Text size="xs" className="text-muted-foreground uppercase tracking-wide font-medium">
              Full Message
            </Text>
            <Box className="rounded-md bg-slate-950 p-3 overflow-auto max-h-64">
              <pre className="text-xs font-mono text-slate-100 whitespace-pre-wrap break-all">
                {log.text}
              </pre>
            </Box>
          </Flex>
          {log.location && (
            <Flex direction="column" gap="xs">
              <Text size="xs" className="text-muted-foreground uppercase tracking-wide font-medium">
                Location
              </Text>
              <Text size="xs" className="font-mono text-muted-foreground">
                {formatLocation(log.location)}
              </Text>
            </Flex>
          )}
        </div>
      )}
    </div>
  );
}

function LegacyConsoleErrors({ consoleLogExcerpt }: { consoleLogExcerpt: string }) {
  return (
    <Box className="rounded-lg border border-border bg-muted/30 p-4 overflow-auto">
      <pre className="text-xs whitespace-pre-wrap font-mono">{consoleLogExcerpt}</pre>
    </Box>
  );
}

export function ConsoleErrorsTab({
  issueId,
  occurrenceId,
  consoleLogExcerpt,
}: ConsoleErrorsTabProps) {
  const [filterMode, setFilterMode] = React.useState<FilterMode>('errors');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [expandedRows, setExpandedRows] = React.useState<Set<number>>(new Set());

  // Fetch console logs lazily from S3
  const consoleLogsQuery = useIssueConsoleLogs(issueId, occurrenceId);
  const consoleLogs = consoleLogsQuery.data ?? null;

  // Loading state
  if (consoleLogsQuery.isLoading) {
    return (
      <div className="px-6 py-6">
        <Flex
          direction="column"
          align="center"
          justify="center"
          className="border border-dashed border-border rounded-lg py-10 space-y-2"
        >
          <LoadingOrbit size="md" />
          <Text size="sm" className="text-muted-foreground">
            Loading console logs...
          </Text>
        </Flex>
      </div>
    );
  }

  // Error state
  if (consoleLogsQuery.isError) {
    return (
      <div className="px-6 py-6">
        <Flex
          direction="column"
          align="center"
          justify="center"
          className="border border-dashed border-border rounded-lg py-10 space-y-2"
        >
          <Warning size={24} className="text-destructive" />
          <Text size="sm" className="text-muted-foreground">
            Failed to load console logs.
          </Text>
        </Flex>
      </div>
    );
  }

  // If we have structured data, use the enhanced view
  const hasStructuredData = consoleLogs && consoleLogs.length > 0;

  if (!hasStructuredData) {
    // Fall back to legacy view
    if (!consoleLogExcerpt || !consoleLogExcerpt.trim()) {
      return (
        <div className="px-6 py-6">
          <EmptyState
            size="sm"
            icon={<Terminal size={22} />}
            title="No console logs"
            description="No console output was captured for this occurrence."
          />
        </div>
      );
    }

    return (
      <div className="px-6 py-6">
        <LegacyConsoleErrors consoleLogExcerpt={consoleLogExcerpt} />
      </div>
    );
  }

  // Filter logs
  const filteredLogs = consoleLogs.filter((log) => {
    // Apply error/warning filter
    if (filterMode === 'errors' && !isErrorOrWarning(log.type)) {
      return false;
    }
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return log.text.toLowerCase().includes(query);
    }
    return true;
  });

  const errorWarningCount = consoleLogs.filter((l) => isErrorOrWarning(l.type)).length;
  const totalCount = consoleLogs.length;

  const toggleRow = (index: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div className="px-6 py-6 space-y-3">
      {/* Toolbar */}
      <Flex align="center" gap="sm" wrap>
        <Button
          variant={filterMode === 'errors' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setFilterMode('errors')}
          iconLeft={<Funnel size={14} />}
        >
          Errors & warnings ({errorWarningCount})
        </Button>
        <Button
          variant={filterMode === 'all' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setFilterMode('all')}
        >
          All logs ({totalCount})
        </Button>
        <div className="flex-1 min-w-48 max-w-xs">
          <TextField
            placeholder="Search messages..."
            value={searchQuery}
            onChange={setSearchQuery}
            leftIcon={<MagnifyingGlass size={14} />}
          />
        </div>
      </Flex>

      {/* Results */}
      {filteredLogs.length === 0 ? (
        <EmptyState
          size="sm"
          icon={<Terminal size={22} />}
          title={searchQuery ? 'No matching logs' : 'No logs to display'}
          description={searchQuery ? 'Try a different search or filter.' : undefined}
        />
      ) : (
        <Box className="rounded-lg border border-border overflow-hidden">
          {filteredLogs.map((log, index) => (
            <ConsoleLogRow
              key={index}
              log={log}
              isExpanded={expandedRows.has(index)}
              onToggle={() => toggleRow(index)}
            />
          ))}
        </Box>
      )}
    </div>
  );
}
